import { type Locale } from '@aysar/contracts';

/** Shown instead of `NaN` / `Infinity` — a number that is not a number is not news. */
const NOT_A_NUMBER = '—';

/**
 * The locale tag to format numbers with.
 *
 * Arabic is pinned to the `latn` numbering system on purpose:
 * `Intl.NumberFormat('ar')` defaults to Arabic-Indic digits (١٢٣), and the
 * design canon calls for Western digits in **both** locales so that an age, a
 * price and a count read identically whichever language the operator is in.
 * It also keeps a number copied out of the portal usable in a spreadsheet.
 */
export function numberLocale(locale: Locale): string {
  return locale === 'ar' ? 'ar-u-nu-latn' : locale;
}

export function formatNumber(
  value: number,
  locale: Locale,
  options?: Intl.NumberFormatOptions,
): string {
  if (!Number.isFinite(value)) {
    return NOT_A_NUMBER;
  }

  return new Intl.NumberFormat(numberLocale(locale), options).format(value);
}

/**
 * A change, with its sign always visible: `+12%`, `-3.5%`, `0%`.
 *
 * The sign is the whole message on a stat card — without it, a red "12%" and a
 * green "12%" are the same string and the colour is doing work that colour alone
 * must never do.
 */
export function formatSignedPercent(value: number, locale: Locale): string {
  if (!Number.isFinite(value)) {
    return NOT_A_NUMBER;
  }

  const formatted = formatNumber(Math.abs(value), locale, { maximumFractionDigits: 1 });

  if (value === 0) {
    return `${formatted}%`;
  }

  // Written out rather than using `signDisplay`, so the sign is always an ASCII
  // `+`/`-` on the inline start of a Western-digit number in both locales.
  return `${value > 0 ? '+' : '-'}${formatted}%`;
}

/**
 * A fraction of one as a percentage — a similarity score, a classifier's
 * confidence, a threshold.
 *
 * Clamped at zero: a cosine similarity is `[-1, 1]`, and a score rendered as
 * "-12%" invites a reviewer to read a magnitude into a number that only means
 * "nothing like it". Nothing this product shows as a percentage is meaningfully
 * negative.
 */
export function formatFraction(value: number, locale: Locale): string {
  if (!Number.isFinite(value)) {
    return NOT_A_NUMBER;
  }

  return formatNumber(Math.max(value, 0), locale, { style: 'percent', maximumFractionDigits: 1 });
}
